/**
 * The one router for the whole app. Every mode + surface is a route here, so the
 * product is a single coherent thing rather than bolted-together parts. Hash-based,
 * self-contained, no legacy dependency.
 *
 *   (empty)        → inspect   (home when no message, report when one is loaded)
 *   #compare       → compare
 *   #corpus        → corpus
 *   #rfcs / #rfc=N → the RFC reference reader
 *   #settings #faq #guide #about #integrations → the document surfaces
 */
import { useCallback, useEffect, useState } from "react";
import { isSurfaceEnabled } from "./features";

export type Route =
  | { kind: "inspect"; sec?: string; hdr?: string }
  | { kind: "compare"; msgs?: string[] }
  | { kind: "triage" }
  | {
      kind: "corpus";
      q?: string;
      tier?: string;
      authfail?: boolean;
      attach?: boolean;
      sort?: string;
      dir?: "asc" | "desc";
    }
  | { kind: "rfcs"; hideObsolete?: boolean; q?: string }
  | { kind: "draft"; slug: string }
  | { kind: "rfc"; n: number; anchor?: string }
  | { kind: "settings"; anchor?: string }
  | { kind: "faq" }
  | { kind: "guide" }
  | { kind: "about" }
  | { kind: "integrations" }
  | { kind: "roadmap" }
  | { kind: "impressum" }
  | { kind: "datenschutz" }
  | { kind: "embed" };

export type RouteKind = Route["kind"];

/** Surfaces reachable from the masthead nav, in order. "Corpus" was opaque:
 * the mode reads a whole mailbox export, so it's labelled Mailbox. */
export const NAV: { kind: RouteKind; label: string }[] = [
  { kind: "triage", label: "Triage" },
  { kind: "compare", label: "Compare" },
  { kind: "corpus", label: "Mailbox" },
  { kind: "rfcs", label: "RFCs" },
  { kind: "guide", label: "Field guide" },
];

/**
 * Param grammar: `base?key=val&key=val` after the route base. Only
 * RE-DERIVABLE state belongs here (filters, sort, section ids, library
 * entry keys): never message content. Values URL-encode through
 * URLSearchParams.
 */
export function parseRoute(hash: string): Route {
  const raw = hash.replace(/^#/, "").trim();
  const qIdx = raw.indexOf("?");
  const h = qIdx === -1 ? raw : raw.slice(0, qIdx);
  const params = new URLSearchParams(qIdx === -1 ? "" : raw.slice(qIdx + 1));
  const str = (k: string) => {
    const v = params.get(k);
    return v ? v : undefined;
  };
  if (h === "compare") {
    const msgs = str("msgs");
    return msgs
      ? { kind: "compare", msgs: msgs.split(",").filter(Boolean) }
      : { kind: "compare" };
  }
  if (h === "triage") return { kind: "triage" };
  if (h === "corpus") {
    const r: Route = { kind: "corpus" };
    if (str("q")) r.q = str("q");
    if (str("tier")) r.tier = str("tier");
    if (params.get("authfail") === "1") r.authfail = true;
    if (params.get("attach") === "1") r.attach = true;
    if (str("sort")) r.sort = str("sort");
    const dir = str("dir");
    if (dir === "asc" || dir === "desc") r.dir = dir;
    return r;
  }
  if (h === "rfcs" || h === "rfcs/current") {
    const r: Route = { kind: "rfcs" };
    if (h === "rfcs/current") r.hideObsolete = true;
    if (str("q")) r.q = str("q");
    return r;
  }
  const sec = /^sec-([a-z]+)$/.exec(h);
  if (sec) return { kind: "inspect", sec: sec[1]! };
  const hdr = /^hdr=([a-z0-9-]+)$/.exec(h);
  if (hdr) return { kind: "inspect", hdr: hdr[1]! };
  const draft = /^draft=([a-z0-9-]+)$/.exec(h);
  if (draft) return { kind: "draft", slug: draft[1]! };
  const rfc = /^rfc=(\d{1,5})(?:\/([a-z0-9-]+))?$/.exec(h);
  if (rfc) {
    return rfc[2]
      ? { kind: "rfc", n: Number(rfc[1]), anchor: rfc[2] }
      : { kind: "rfc", n: Number(rfc[1]) };
  }
  const setm = /^settings(?:\/([a-z-]+))?$/.exec(h);
  if (setm) {
    return setm[1] ? { kind: "settings", anchor: setm[1] } : { kind: "settings" };
  }
  if (h === "faq") return { kind: "faq" };
  if (h === "guide" || h === "docs") return { kind: "guide" };
  if (h === "about") return { kind: "about" };
  // Internal-only in a built bundle (see ./features). Gated HERE rather than
  // only where the links are rendered, because hiding a link does not close a
  // route: `#roadmap` survives in bookmarks, in a pasted URL and in anyone's
  // history, and would otherwise still open the surface in production. Falling
  // through to the `inspect` default is deliberate: an unknown hash already
  // lands on Inspect, so a withheld surface is indistinguishable from one that
  // never existed, rather than announcing itself with a "not available here".
  if (h === "integrations" && isSurfaceEnabled("integrations")) {
    return { kind: "integrations" };
  }
  if (h === "roadmap" && isSurfaceEnabled("roadmap")) return { kind: "roadmap" };
  if (h === "impressum") return { kind: "impressum" };
  if (h === "datenschutz") return { kind: "datenschutz" };
  if (h === "embed") return { kind: "embed" };
  return { kind: "inspect" };
}

export function routeToHash(r: Route): string {
  const withParams = (base: string, params: URLSearchParams): string => {
    const s = params.toString();
    return s ? `${base}?${s}` : base;
  };
  switch (r.kind) {
    case "inspect":
      if (r.hdr) return `hdr=${r.hdr}`;
      return r.sec ? `sec-${r.sec}` : "";
    case "compare": {
      const p = new URLSearchParams();
      if (r.msgs?.length) p.set("msgs", r.msgs.join(","));
      return withParams("compare", p);
    }
    case "corpus": {
      const p = new URLSearchParams();
      if (r.q) p.set("q", r.q);
      if (r.tier) p.set("tier", r.tier);
      if (r.authfail) p.set("authfail", "1");
      if (r.attach) p.set("attach", "1");
      if (r.sort) p.set("sort", r.sort);
      if (r.dir) p.set("dir", r.dir);
      return withParams("corpus", p);
    }
    case "rfc":
      return r.anchor ? `rfc=${r.n}/${r.anchor}` : `rfc=${r.n}`;
    case "settings":
      return r.anchor ? `settings/${r.anchor}` : "settings";
    case "rfcs": {
      const p = new URLSearchParams();
      if (r.q) p.set("q", r.q);
      return withParams(r.hideObsolete ? "rfcs/current" : "rfcs", p);
    }
    case "draft":
      return `draft=${r.slug}`;
    default:
      return r.kind;
  }
}

export interface Router {
  route: Route;
  navigate: (r: Route) => void;
  /**
   * Update the CURRENT route's transient params in place: replaceState (no
   * new history entry, no scroll reset), then a synthetic hashchange so
   * every useRoute instance resyncs. For filters, sort, the active report
   * section: state a reload or Back should restore, but typing must not
   * spam history with.
   */
  replace: (r: Route) => void;
  /** Convenience for masthead links/buttons. */
  go: (kind: RouteKind) => void;
}

export function useRoute(): Router {
  const [route, setRoute] = useState<Route>(() =>
    typeof window === "undefined"
      ? { kind: "inspect" }
      : parseRoute(window.location.hash),
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onHash = () => setRoute(parseRoute(window.location.hash));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const navigate = useCallback((r: Route) => {
    if (typeof window === "undefined") {
      setRoute(r);
      return;
    }
    const h = routeToHash(r);
    // RFC section navigation keeps the scroll position; the reader scrolls to the
    // section anchor itself, so we must not reset to the top here.
    const keepScroll = r.kind === "rfc" && !!r.anchor;
    const current = window.location.hash.replace(/^#/, "");
    if (h === current) {
      // Already on this route (or both empty), so resync this instance + scroll.
      setRoute(r);
      if (!keepScroll) window.scrollTo(0, 0);
      return;
    }
    // Drive navigation through the URL hash so the NATIVE hashchange fires and
    // EVERY useRoute instance (masthead, footer, the RFC reader) resyncs.
    // pushState would update only the calling component's state and leave the
    // rest stale, including the shell that actually renders the page.
    if (h) {
      window.location.hash = `#${h}`;
    } else {
      // pushState, not replaceState: clearing the hash for Inspect must be a
      // REAL history entry, or Back from Inspect is a no-op and the previous
      // mode becomes unreachable. The h === current guard above prevents
      // duplicate entries.
      history.pushState(null, "", window.location.pathname + window.location.search);
      window.dispatchEvent(new HashChangeEvent("hashchange"));
    }
    if (!keepScroll) window.scrollTo(0, 0);
  }, []);

  const replace = useCallback((r: Route) => {
    if (typeof window === "undefined") {
      setRoute(r);
      return;
    }
    const h = routeToHash(r);
    const base = window.location.pathname + window.location.search;
    history.replaceState(null, "", h ? `${base}#${h}` : base);
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  }, []);

  const go = useCallback((kind: RouteKind) => navigate({ kind } as Route), [navigate]);

  return { route, navigate, replace, go };
}
